import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";
import "./blocknote.css";
import React, { useEffect, useState } from "react";
import YPartyKitProvider from "y-partykit/provider";
import * as Y from "yjs";
import {
    BlockNoteEditor,
    BlockNoteSchema,
    defaultBlockSpecs,
    filterSuggestionItems,
    PartialBlock,
    combineByGroup,
} from "@blocknote/core";
import {
    DefaultReactSuggestionItem,
    FilePanelController,
    FormattingToolbar,
    FormattingToolbarController,
    getDefaultReactSlashMenuItems,
    getFormattingToolbarItems,
    SuggestionMenuController,
    SuggestionMenuProps,
} from "@blocknote/react";
import { Label } from "@/components/ui/label";
import { IAccountAssets, IUserData } from "@/types";
import { DorasFilePanel } from "./ImagePicker";
import { FileReplaceButton } from "./FileReplaceButton";
import { CustomBlock } from "./TemplateBlock";
import { ButtonBlock, insertButtonBlock } from "./Button";
import { SeparatorBlock, insertSeparatorBlock } from "./Separator";
import { QuoteBlock, insertQuoteBlock } from "./Quote";
import { VideoBlock, insertVideoBlock } from "./Video";
import { en } from "@blocknote/core/locales";
import {
    getMultiColumnSlashMenuItems,
    multiColumnDropCursor,
    locales as multiColumnLocales,
    withMultiColumn,
} from "@blocknote/xl-multi-column";
interface Props {
    id: string;
    value?: any;
    onChange?: (value: any) => void;
    readOnly?: boolean;
    assets?: IAccountAssets[];
    account_id?: string;
    _setAssets?: (assets: IAccountAssets[]) => void;
    brand_id?: any;
    updateContent?: PartialBlock[] | undefined | "loading";
    collaborative?: boolean;
    roomId?: string;
    collaborationDoc?: Y.Doc;
    collaborationProvider?: YPartyKitProvider;
    collaborationUser?: { name: string; color: string };
    account?: IUserData;
}

const schema = BlockNoteSchema.create({
    blockSpecs: {
        ...defaultBlockSpecs,
        customblocktype: CustomBlock,
        buttonblock: ButtonBlock,
        separatorblock: SeparatorBlock,
        quoteblock: QuoteBlock,
        videoblock: VideoBlock,
    },
});

// Custom component to replace the default Slash Menu.
function CustomSlashMenu(
    props: SuggestionMenuProps<DefaultReactSuggestionItem>
) {
    return (
        <div
            className={
                "slash-menu max-h-96 w-fit max-w-96 overflow-auto rounded-md border-2 bg-card px-1 pb-1 shadow-xl"
            }
        >
            {Object.entries(
                props.items.reduce(
                    (acc, item) => {
                        if (
                            item.title !== "File" &&
                            item.title !== "Video" &&
                            item.title !== "Check List" &&
                            item.title !== "Audio"
                        ) {
                            if (!acc[item.group as any]) {
                                acc[item.group as any] = [];
                            }
                            acc[item.group as any].push(item);
                        }
                        return acc;
                    },
                    {} as Record<string, typeof props.items>
                )
            ).map(([group, items]) => (
                <div key={group}>
                    <div className="flex flex-col gap-2 py-1">
                        <Label
                            variant="toggle"
                            className="mb-0 px-3 pb-0 text-base font-bold"
                        >
                            {group}
                        </Label>
                    </div>
                    {items.map((item, index) => (
                        <div
                            key={item.title}
                            className={`slash-menu-item flex w-72 items-center gap-3 rounded-md bg-transparent p-2 transition-all duration-300 hover:bg-accent ${props.selectedIndex === index ? " selected" : ""
                                }`}
                            onClick={() => {
                                props.onItemClick?.(item);
                            }}
                        >
                            <div className="shrink-0 rounded-md bg-muted p-2">
                                {item.icon}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label
                                    variant="toggle"
                                    className="mb-0 pb-0 text-xs font-bold"
                                >
                                    {item.title}
                                </Label>
                                <Label
                                    variant="subtoggle"
                                    className="mb-0 pb-0 text-xs"
                                >
                                    {item.subtext}
                                </Label>
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
const EditorComponent = ({
    id,
    value,
    onChange,
    readOnly,
    assets,
    account_id,
    _setAssets,
    brand_id,
    updateContent,
    collaborative = false,
    roomId = "default-room",
    collaborationDoc,
    collaborationProvider,
    collaborationUser,
    account,
}: Props) => {
    const [_assets, setAssets] = useState<IAccountAssets[]>(assets || []);
    const [loading, setLoading] = useState(true);
    const [initialContent, setInitialContent] = useState<
        PartialBlock[] | undefined | "loading"
    >(value || "loading");
    useEffect(() => {
        if (updateContent) {
            setInitialContent(updateContent);
        }
    }, [updateContent]);

    useEffect(() => {
        _setAssets?.(_assets);
    }, [_assets, _setAssets]);

    const editor = React.useMemo(() => {
        // For collaborative mode, we don't need to wait for initialContent
        if (collaborationDoc && collaborationProvider && collaborationUser) {
            console.log("Creating collaborative editor", { collaborationUser });
            setLoading(false);
            return BlockNoteEditor.create({
                collaboration: {
                    provider: collaborationProvider,
                    fragment: collaborationDoc.getXmlFragment(
                        "document-store"
                    ) as any,
                    user: collaborationUser,
                },
                trailingBlock: readOnly ? false : true,
                dropCursor: multiColumnDropCursor,
                schema: withMultiColumn(schema),
                dictionary: {
                    ...en,
                    multi_column: multiColumnLocales.en,
                },
            });
        }

        // For non-collaborative mode, we need to wait for initialContent
        if (initialContent === "loading") {
            return undefined;
        }

        // Regular editor creation logic
        if (initialContent && initialContent.length === 0) {
            setLoading(false);
            return BlockNoteEditor.create({
                trailingBlock: readOnly ? false : true,
                dropCursor: multiColumnDropCursor,
                schema: withMultiColumn(schema),

                dictionary: {
                    ...en,
                    multi_column: multiColumnLocales.en,
                },
            });
        }

        setLoading(false);
        return BlockNoteEditor.create({
            initialContent,
            trailingBlock: readOnly ? false : true,
            dropCursor: multiColumnDropCursor,
            schema: withMultiColumn(schema),
            dictionary: {
                ...en,
                multi_column: multiColumnLocales.en,
            },
            domAttributes: {
                inlineContent: {
                    class: "scroll-mt-16 hello-world-block",
                },
                blockGroup: {
                    class: `FontBody rich-text-block-content-${id}`
                }
            },
        });
    }, [
        initialContent,
        readOnly,
        collaborationDoc,
        collaborationProvider,
        collaborationUser,
    ]);

    // Add debug logging
    useEffect(() => {
        console.log("Editor state:", {
            editor: !!editor,
            loading,
            collaborative: !!collaborationDoc,
            initialContent: initialContent === "loading" ? "loading" : "loaded",
        });
    }, [editor, loading, collaborationDoc, initialContent]);

    useEffect(() => {
        if (!loading && editor) {
            window.dispatchEvent(new CustomEvent("BlockNote-Editor-Ready"));
            if (!collaborationDoc) {
                editor.onChange(() => {
                    onChange?.(editor.document);
                });
            }
            // Patch all <a> tags inside the BlockNoteView to open in the same tab
            const blockNoteRoot = document.querySelector(
                `#blocknote-editor-${id}`
            );
            if (blockNoteRoot) {
                const linkElements = blockNoteRoot.querySelectorAll(
                    "a[target='_blank'], a[target='_self']"
                );
                linkElements.forEach((element) => {
                    element.setAttribute("target", "_self");
                });
                // Add or remove class to disable pointer events on links in edit mode
                if (!readOnly) {
                    blockNoteRoot.classList.add("blocknote-disable-links");
                } else {
                    blockNoteRoot.classList.remove("blocknote-disable-links");
                }
                // Event delegation: prevent navigation if editor is editable
                const delegatedClickHandler = (e: Event) => {
                    if (!readOnly) {
                        const target = e.target as HTMLElement;
                        const anchor = target.closest("a");
                        if (anchor) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }
                };
                blockNoteRoot.addEventListener("click", delegatedClickHandler);
                // Cleanup event listener on unmount or dependency change
                return () => {
                    blockNoteRoot.removeEventListener(
                        "click",
                        delegatedClickHandler
                    );
                };
            }
        }
    }, [editor, loading, readOnly]);
    if (editor === undefined) {
        console.log("Editor is undefined, showing loading message");
        return "Loading content...";
    }

    if (loading && !collaborationDoc) {
        return "loading";
    }
    return (
        <BlockNoteView
            theme="dark"
            editor={editor}
            editable={!readOnly}
            slashMenu={false}
            id={"blocknote-editor-" + id}
            key={"blocknote-editor-" + id}
            className={`${readOnly && ""}`}
            data-theming-readonly={readOnly ? "true" : "false"}
            data-themeing-global
            formattingToolbar={false}
            filePanel={false}
        >
            {/* @ts-ignore */}
            <SuggestionMenuController
                triggerCharacter={"/"}
                // @ts-ignore
                suggestionMenuComponent={CustomSlashMenu}
                getItems={async (query) =>
                    // Gets all default slash menu items and `insertAlert` item.
                    filterSuggestionItems(
                        [
                            ...combineByGroup(
                                getDefaultReactSlashMenuItems(editor),
                                getMultiColumnSlashMenuItems(editor)
                            ),
                            insertButtonBlock(),
                            insertSeparatorBlock(),
                            insertQuoteBlock(),
                            insertVideoBlock(),
                        ],
                        query
                    )
                }
            />
            <FormattingToolbarController
                formattingToolbar={(props) => {
                    // Replaces default file replace button with one that opens Uppy.
                    // suggestionMenuComponent={CustomSlashMenu}
                    // Custom menu component commented out for now
                    const items = getFormattingToolbarItems();
                    items.splice(
                        items.findIndex((c) => c.key === "replaceFileButton"),
                        1,
                        <FileReplaceButton
                            key={"fileReplaceButton"}
                            assets={_assets}
                            setAssets={setAssets}
                            account_id={account_id}
                            brand_id={brand_id}
                        />
                    );

                    return (
                        <FormattingToolbar {...props}>
                            {items}
                        </FormattingToolbar>
                    );
                }}
            />
            <FilePanelController
                filePanel={(props) => (
                    <DorasFilePanel
                        props={props}
                        assets={_assets}
                        setAssets={setAssets}
                        account_id={account_id}
                        brand_id={brand_id}
                        account={account}
                    />
                )}
            />
        </BlockNoteView>
    );
};
export default EditorComponent;
